/**
 * CAMASA CPS — Módulo Medições (Orçado vs Realizado).
 *
 * 3 estados:
 *  1. Empty: upload de PDF de orçamento → IA extrai estrutura
 *  2. Preview: tabela editável (admin confere antes de salvar)
 *  3. Saved: resumo + lista por ambiente (Etapas 2-5 expandirão)
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import {
  Sparkles, AlertCircle, FileText, Trash2, Ruler, Plus, X,
  CheckCircle2, Download, MessageCircle, RefreshCw
} from "lucide-react";
import { API, brl, fmt, UNIDADES_MED, BACKEND, fileUrl } from "../lib";

/* ========== MEDIÇÕES (Orçado vs Realizado) — Etapa 1: Importar Orçamento via IA ========== */

export function ObraMedicoesPanel({ obra, api, onReload }) {
  const [medicoes, setMedicoes] = useState(null);
  const [totais, setTotais] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState("");
  const [importPct, setImportPct] = useState(0);
  const [preview, setPreview] = useState(null); // {ambientes, filename}
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api("get", `/obras/${obra.id}/medicoes`);
      setMedicoes(r.medicoes);
      setTotais(r.totais);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [api, obra.id]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      alert("Envie um arquivo PDF (.pdf).");
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      alert("PDF muito grande (limite 25MB).");
      return;
    }
    setImporting(true);
    setImportProgress("Enviando PDF ao servidor…");
    setImportPct(0);
    try {
      const fd = new FormData();
      fd.append("file", f);
      console.log("[Medicoes] POST /medicoes/import iniciado — arquivo:", f.name, f.size, "bytes");
      const r = await axios.post(`${API}/obras/${obra.id}/medicoes/import`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem("cps_token")}` },
        timeout: 60000, // 60s só pra resposta inicial (que volta em <1s normalmente — é só o job_id)
      });
      console.log("[Medicoes] POST response:", r.data);

      // Modo assíncrono: polling tolerante a servidor ocupado.
      // Estratégia:
      // - Orçamento total: 600s (10min). Após isso → erro fatal de timeout.
      // - Falhas de poll (network/timeout/5xx) NÃO abortam — apenas informam
      //   "servidor ocupado" e continuam tentando. O job está vivo no
      //   servidor independente do polling.
      // - Aborta IMEDIATAMENTE só se o job retornar status="error"|"failed".
      if (r.data?.async && r.data?.job_id) {
        const jobId = r.data.job_id;
        setImportProgress("Conectando ao processamento…");
        const TOTAL_BUDGET_MS = 1_200_000;   // 20min total — só então erro fatal
        const SOFT_WARN_AFTER_MS = 300_000;  // 5min sem qualquer sinal → mensagem mais firme
        const POLL_INTERVAL_MS = 3000;
        const startedAt = Date.now();
        let lastSuccessAt = startedAt;       // última vez que o servidor respondeu o poll
        let attempts = 0;
        // Primeira espera curta (1s) pra feedback quase imediato
        await new Promise(res => setTimeout(res, 1000));
        while (true) {
          attempts++;
          const elapsed = Date.now() - startedAt;
          if (elapsed > TOTAL_BUDGET_MS) {
            throw new Error(
              `Tempo esgotado — extração não concluiu em ${Math.floor(TOTAL_BUDGET_MS / 60_000)} minutos. ` +
              `Tente um PDF menor ou mais simples. ` +
              `(Job ${jobId.slice(0, 8)}… pode continuar processando em segundo plano.)`
            );
          }
          let job;
          try {
            job = await api("get", `/obras/${obra.id}/medicoes/import/job/${jobId}`);
            lastSuccessAt = Date.now();
            console.log(`[Medicoes] poll #${attempts} (${Math.floor(elapsed / 1000)}s):`, job.status, job.progress_pct, job.progress_msg);
          } catch (er) {
            const silentFor = Date.now() - lastSuccessAt;
            console.warn(`[Medicoes] poll #${attempts} falhou (silêncio ${Math.floor(silentFor / 1000)}s, total ${Math.floor(elapsed / 1000)}s):`, er?.response?.status, er?.message);
            // Mensagem amigável conforme tempo de silêncio. NUNCA aborta aqui.
            if (silentFor >= SOFT_WARN_AFTER_MS) {
              setImportProgress(
                `Aguardando processamento… servidor ocupado há ${Math.floor(silentFor / 1000)}s. ` +
                `O job continua rodando em segundo plano.`
              );
            } else {
              setImportProgress("Aguardando processamento… servidor ocupado");
            }
            await new Promise(res => setTimeout(res, POLL_INTERVAL_MS));
            continue;
          }
          if (job.progress_msg) setImportProgress(job.progress_msg);
          if (typeof job.progress_pct === "number") setImportPct(job.progress_pct);
          if (job.status === "done") {
            const data = job.result || {};
            console.log("[Medicoes] Extração concluída:", data.meta);
            setPreview({ ...data.preview, filename: data.filename, meta: data.meta });
            return;
          }
          if (job.status === "error" || job.status === "failed") {
            throw new Error(job.error || "Erro desconhecido na extração");
          }
          await new Promise(res => setTimeout(res, POLL_INTERVAL_MS));
        }
      } else {
        setPreview({ ...r.data.preview, filename: r.data.filename, meta: r.data.meta });
      }
    } catch (err) {
      console.error("[Medicoes] Erro no upload:", err);
      const detalhe = err?.response?.data?.detail || err?.message || "Erro desconhecido";
      alert("❌ Erro na extração: " + detalhe);
    } finally {
      setImporting(false);
      setImportProgress("");
      setImportPct(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const reimportar = async () => {
    if (!window.confirm("Reimportar Orçamento apaga TODOS os dados de medições desta obra (orçado + realizado + ambientes fechados). Continuar?")) return;
    try {
      await api("delete", `/obras/${obra.id}/medicoes`);
      setMedicoes(null);
      setTotais(null);
      setPreview(null);
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    }
  };

  if (loading) return <div className="page-sub">Carregando medições…</div>;

  // Tela 1: Preview editável (após upload + extração IA)
  if (preview) {
    return (
      <MedicoesPreview
        preview={preview}
        obraId={obra.id}
        api={api}
        onCancel={() => setPreview(null)}
        onSaved={async () => { setPreview(null); await load(); onReload && onReload(); }}
      />
    );
  }

  // Tela 2: Já existe orçamento salvo → mostra resumo
  if (medicoes) {
    return (
      <>
        <MedicoesSavedView
          medicoes={medicoes}
          totais={totais}
          obraId={obra.id}
          api={api}
          onReimportar={reimportar}
          onReload={load}
        />
        <div className="container">
          <OrcamentosExtrasSection
            obraId={obra.id}
            extras={medicoes.orcamentos_extras || []}
            totais={totais}
            api={api}
            onChanged={load}
          />
        </div>
      </>
    );
  }

  // Tela 3: Empty state — upload PDF
  return (
    <div className="container" style={{ paddingTop: 16 }}>
      <h2 className="page-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Ruler size={22} /> Medições — Orçado vs Realizado
      </h2>
      <p className="page-sub" style={{ marginBottom: 28 }}>
        Importe o PDF do orçamento descritivo desta obra. Nossa IA (Claude Sonnet 4.5) extrai
        automaticamente <strong>ambientes</strong>, <strong>itens</strong>, <strong>quantidades</strong> e <strong>valores</strong> em segundos.
        Você confere e edita antes de salvar.
      </p>

      <div
        className="panel"
        style={{
          padding: 32,
          textAlign: "center",
          border: "2px dashed var(--gold)",
          borderRadius: 12,
          background: "rgba(184, 147, 90, 0.04)",
        }}
        data-testid="medicoes-empty-state"
      >
        <FileText size={42} color="var(--gold)" strokeWidth={1.4} style={{ margin: "0 auto 12px" }} />
        <h3 style={{ margin: "0 0 8px", fontSize: 18 }}>Nenhum orçamento importado ainda</h3>
        <p className="page-sub" style={{ marginBottom: 20 }}>
          Suba o PDF original do orçamento. Limite 25MB.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf"
          style={{ display: "none" }}
          onChange={handleUpload}
          data-testid="medicoes-pdf-input"
        />
        <button
          className="btn"
          style={{ background: "var(--gold)", color: "#0a0a0a", fontWeight: 600 }}
          onClick={() => fileRef.current?.click()}
          disabled={importing}
          data-testid="medicoes-upload-btn"
        >
          {importing ? "✨ Extraindo com IA…" : "📄 Importar Orçamento (PDF)"}
        </button>
        {importing && (
          <div style={{ marginTop: 16 }}>
            <p className="page-sub" style={{ fontSize: 13, marginBottom: 10 }} data-testid="import-progress-msg">
              {importProgress || "Iniciando…"}
            </p>
            {importPct > 0 && (
              <div style={{ height: 6, background: "var(--bg-3)", borderRadius: 3, overflow: "hidden" }}>
                <div
                  style={{
                    width: `${importPct}%`, height: "100%",
                    background: "var(--gold)", transition: "width .4s ease",
                  }}
                  data-testid="import-progress-bar"
                />
              </div>
            )}
            <p style={{ fontSize: 11, opacity: 0.7, marginTop: 8 }}>
              Processamos o PDF em partes de 4 páginas para respeitar o limite da IA. Timeout: 90s por parte.
            </p>
          </div>
        )}
      </div>

      <OrcamentosExtrasSection
        obraId={obra.id}
        extras={[]}
        totais={null}
        api={api}
        onChanged={load}
      />
    </div>
  );
}

export function MedicoesPreview({ preview, obraId, api, onCancel, onSaved }) {
  const [ambientes, setAmbientes] = useState(() =>
    (preview.ambientes || []).map((a) => ({
      ...a,
      itens: (a.itens || []).map((it) => ({ ...it })),
    }))
  );
  const [saving, setSaving] = useState(false);

  const updateItem = (ambIdx, itIdx, field, value) => {
    setAmbientes((prev) => {
      const next = [...prev];
      const amb = { ...next[ambIdx], itens: [...next[ambIdx].itens] };
      const it = { ...amb.itens[itIdx], [field]: value };
      // Recalcula valor_final ao editar qtd, valor_unitario ou desconto
      if (["qtd_orcada", "valor_unitario", "desconto_pct"].includes(field)) {
        const q = Number(field === "qtd_orcada" ? value : it.qtd_orcada) || 0;
        const vu = Number(field === "valor_unitario" ? value : it.valor_unitario) || 0;
        const d = Number(field === "desconto_pct" ? value : it.desconto_pct) || 0;
        it.valor_final = Number((q * vu * (1 - d / 100)).toFixed(2));
      }
      amb.itens[itIdx] = it;
      next[ambIdx] = amb;
      return next;
    });
  };

  const addItem = (ambIdx) => {
    setAmbientes((prev) => {
      const next = [...prev];
      const amb = { ...next[ambIdx], itens: [...next[ambIdx].itens] };
      amb.itens.push({
        item: "",
        unidade: "m²",
        qtd_orcada: 0,
        valor_unitario: 0,
        desconto_pct: 0,
        valor_final: 0,
      });
      next[ambIdx] = amb;
      return next;
    });
  };

  const removeItem = (ambIdx, itIdx) => {
    setAmbientes((prev) => {
      const next = [...prev];
      const amb = { ...next[ambIdx], itens: next[ambIdx].itens.filter((_, i) => i !== itIdx) };
      next[ambIdx] = amb;
      return next;
    });
  };

  const updateAmbNome = (ambIdx, v) => {
    setAmbientes((prev) => {
      const next = [...prev];
      next[ambIdx] = { ...next[ambIdx], nome: v };
      return next;
    });
  };

  const removeAmbiente = (ambIdx) => {
    if (!window.confirm("Remover este ambiente e todos os seus itens?")) return;
    setAmbientes((prev) => prev.filter((_, i) => i !== ambIdx));
  };

  const addAmbiente = () => {
    setAmbientes((prev) => [...prev, { nome: "Novo Ambiente", itens: [] }]);
  };

  const totalGeral = useMemo(() => {
    return ambientes.reduce(
      (sum, amb) => sum + (amb.itens || []).reduce((s, it) => s + (Number(it.valor_final) || 0), 0),
      0
    );
  }, [ambientes]);

  const totalItens = useMemo(() => {
    return ambientes.reduce((s, a) => s + (a.itens?.length || 0), 0);
  }, [ambientes]);

  const save = async () => {
    if (!ambientes.length || totalItens === 0) {
      alert("Adicione ao menos um item antes de salvar.");
      return;
    }
    if (!window.confirm(`Confirmar e salvar orçamento com ${ambientes.length} ambiente(s) e ${totalItens} item(ns)? Após salvar, a parte "Orçado" ficará bloqueada para edição.`)) return;
    setSaving(true);
    try {
      await api("post", `/obras/${obraId}/medicoes/save`, {
        ambientes,
        filename: preview.filename,
      });
      onSaved();
    } catch (e) {
      alert("Erro ao salvar: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container" style={{ paddingTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 18, flexWrap: "wrap" }}>
        <div>
          <h2 className="page-title" style={{ display: "flex", alignItems: "center", gap: 10, margin: 0 }}>
            <Sparkles size={20} color="var(--gold)" /> Pré-visualização do Orçamento
          </h2>
          <p className="page-sub" style={{ margin: "6px 0 0" }}>
            Confira <strong>cada item</strong> antes de salvar. {preview.filename && <span style={{ color: "var(--gold)" }}>· {preview.filename}</span>}
            {preview.meta?.num_paginas && <span> · {preview.meta.num_paginas} página(s)</span>}
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>Total Orçado</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: "var(--gold)" }} data-testid="medicoes-preview-total">
            {brl(totalGeral)}
          </div>
          <div style={{ fontSize: 11, color: "var(--muted)" }}>{ambientes.length} ambiente(s) · {totalItens} item(ns)</div>
        </div>
      </div>

      {/* Banner de completude — sempre visível pra reforçar a conferência */}
      {preview.meta && (
        <div
          className="panel"
          style={{
            padding: 14,
            marginBottom: 18,
            background: preview.meta.completude_alerta ? "rgba(229, 93, 93, 0.08)" : "rgba(184, 147, 90, 0.06)",
            border: preview.meta.completude_alerta ? "1px solid #e35d5d" : "1px solid var(--gold)",
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}
          data-testid="medicoes-completude-banner"
        >
          <AlertCircle size={18} color={preview.meta.completude_alerta ? "#e35d5d" : "var(--gold)"} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 13, lineHeight: 1.5 }}>
            {preview.meta.completude_alerta ? (
              <>
                <strong style={{ color: "#e35d5d" }}>Possível omissão detectada.</strong> {preview.meta.completude_alerta}
                <br />
                <span style={{ color: "var(--muted)", fontSize: 12 }}>
                  Heurística: {preview.meta.linhas_com_valor_pdf} linhas com valores no PDF · IA extraiu {preview.meta.itens_extraidos} itens.
                </span>
              </>
            ) : (
              <>
                <strong>IA extraiu {preview.meta.itens_extraidos} item(ns)</strong> em {preview.meta.ambientes_extraidos} ambiente(s) a partir de {preview.meta.num_paginas} página(s).
                <span style={{ color: "var(--muted)", marginLeft: 6 }}>
                  Confira <strong>linha por linha</strong> e <strong>compare com o PDF original</strong> antes de salvar — nenhum item pode faltar.
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {ambientes.map((amb, ambIdx) => (
        <div key={ambIdx} className="panel" style={{ marginBottom: 20, padding: 18 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
            <input
              className="form-input"
              value={amb.nome || ""}
              onChange={(e) => updateAmbNome(ambIdx, e.target.value)}
              placeholder="Nome do ambiente"
              style={{ fontSize: 16, fontWeight: 600, color: "var(--gold)", maxWidth: 320 }}
              data-testid={`med-amb-nome-${ambIdx}`}
            />
            <span style={{ fontSize: 12, color: "var(--muted)", marginLeft: 6 }}>
              {amb.itens?.length || 0} item(ns)
            </span>
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => addItem(ambIdx)} data-testid={`med-add-item-${ambIdx}`}>
                <Plus size={13} /> Item
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => removeAmbiente(ambIdx)} title="Remover ambiente">
                <Trash2 size={13} />
              </button>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="tbl" style={{ width: "100%", fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ minWidth: 280 }}>Item</th>
                  <th style={{ width: 80 }}>Un.</th>
                  <th style={{ width: 110 }}>Qtd Orçada</th>
                  <th style={{ width: 130 }}>Vl. Unitário</th>
                  <th style={{ width: 140 }}>Vl. Final</th>
                  <th style={{ width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {(amb.itens || []).map((it, itIdx) => (
                  <tr key={itIdx}>
                    <td>
                      <input
                        className="form-input form-input-sm"
                        value={it.item || ""}
                        onChange={(e) => updateItem(ambIdx, itIdx, "item", e.target.value)}
                        placeholder="Descrição do item"
                        data-testid={`med-item-${ambIdx}-${itIdx}`}
                      />
                    </td>
                    <td>
                      <select
                        className="form-input form-input-sm"
                        value={it.unidade || "m²"}
                        onChange={(e) => updateItem(ambIdx, itIdx, "unidade", e.target.value)}
                      >
                        {UNIDADES_MED.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number" step="0.01" min="0"
                        className="form-input form-input-sm"
                        value={it.qtd_orcada ?? 0}
                        onChange={(e) => updateItem(ambIdx, itIdx, "qtd_orcada", e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number" step="0.01" min="0"
                        className="form-input form-input-sm"
                        value={it.valor_unitario ?? 0}
                        onChange={(e) => updateItem(ambIdx, itIdx, "valor_unitario", e.target.value)}
                      />
                    </td>
                    <td style={{ fontWeight: 600, color: "var(--gold)" }}>
                      {brl(Number(it.valor_final) || 0)}
                    </td>
                    <td>
                      <button className="btn btn-ghost btn-sm" onClick={() => removeItem(ambIdx, itIdx)} title="Remover item" style={{ padding: "4px 6px" }}>
                        <X size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
                {(!amb.itens || amb.itens.length === 0) && (
                  <tr><td colSpan={6} style={{ textAlign: "center", color: "var(--muted)", padding: 16 }}>Nenhum item neste ambiente.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
        <button className="btn btn-ghost btn-sm" onClick={addAmbiente}>
          <Plus size={13} /> Adicionar Ambiente
        </button>
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", borderTop: "1px solid var(--border)", paddingTop: 18 }}>
        <button className="btn btn-ghost" onClick={onCancel} disabled={saving} data-testid="med-cancel-btn">
          Cancelar
        </button>
        <button
          className="btn"
          style={{ background: "var(--gold)", color: "#0a0a0a", fontWeight: 600 }}
          onClick={save}
          disabled={saving}
          data-testid="med-save-btn"
        >
          {saving ? "Salvando…" : "✓ Confirmar e Salvar Orçamento"}
        </button>
      </div>
    </div>
  );
}

export function MedicoesSavedView({ medicoes, totais, obraId, api, onReimportar, onReload }) {
  const [descInput, setDescInput] = useState(() => String(medicoes?.desconto_global_pct ?? 0));
  const [savingDesc, setSavingDesc] = useState(false);
  const ambientes = medicoes?.ambientes || [];
  const allFechados = ambientes.length > 0 && ambientes.every((a) => a.fechado);

  // Sincroniza input quando vem novo desconto do servidor
  useEffect(() => {
    setDescInput(String(medicoes?.desconto_global_pct ?? 0));
  }, [medicoes?.desconto_global_pct]);

  const saveDesconto = async (raw) => {
    const v = Number(String(raw).replace(",", "."));
    if (Number.isNaN(v) || v < 0 || v > 100) {
      alert("Desconto deve ser entre 0 e 100.");
      setDescInput(String(medicoes?.desconto_global_pct ?? 0));
      return;
    }
    if (v === Number(medicoes?.desconto_global_pct ?? 0)) return;
    setSavingDesc(true);
    try {
      await api("patch", `/obras/${obraId}/medicoes/desconto`, { desconto_global_pct: v });
      onReload && onReload();
    } catch (e) {
      alert("Erro ao salvar desconto: " + (e?.response?.data?.detail || e.message));
      setDescInput(String(medicoes?.desconto_global_pct ?? 0));
    } finally {
      setSavingDesc(false);
    }
  };

  return (
    <div className="container" style={{ paddingTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 18, flexWrap: "wrap" }}>
        <div>
          <h2 className="page-title" style={{ display: "flex", alignItems: "center", gap: 10, margin: 0 }}>
            <Ruler size={20} /> Medições — Orçado vs Realizado
          </h2>
          <p className="page-sub" style={{ margin: "6px 0 0" }}>
            Orçamento importado em {medicoes.criado_em ? fmt(medicoes.criado_em) : "—"}
            {medicoes.criado_por && ` por ${medicoes.criado_por}`}
            {medicoes.orcamento_filename && <span style={{ color: "var(--gold)" }}> · {medicoes.orcamento_filename}</span>}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <TermoActionButton medicoes={medicoes} obraId={obraId} api={api} onChanged={onReload} />
          <button className="btn btn-ghost btn-sm" onClick={onReimportar} data-testid="med-reimport-btn" title="Apaga tudo e começa de novo">
            <Trash2 size={13} /> Reimportar Orçamento
          </button>
        </div>
      </div>

      {/* Painel do Termo Final (se gerado) */}
      <TermoFinalPanel medicoes={medicoes} obraId={obraId} api={api} onChanged={onReload} />

      {/* Painel Resumo: Bruto → −Desconto → Líquido → Realizado → Saldo */}
      {totais && (
        <div className="panel" style={{ padding: 20, marginBottom: 22 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 18 }}>
            <div>
              <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>Orçado Bruto</div>
              <div style={{ fontSize: 20, fontWeight: 600 }} data-testid="med-total-bruto">{brl(totais.total_orcado_bruto ?? totais.total_orcado)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>Desconto Global</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <input
                  type="number" step="0.01" min="0" max="100"
                  className="form-input form-input-sm"
                  value={descInput}
                  onChange={(e) => setDescInput(e.target.value)}
                  onBlur={(e) => saveDesconto(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  disabled={savingDesc || allFechados}
                  title={allFechados ? "Todos os ambientes fechados — desconto travado" : "Edita e Tab/Enter para salvar"}
                  style={{ width: 80, textAlign: "right", fontWeight: 600, color: "var(--gold)" }}
                  data-testid="med-desconto-input"
                />
                <span style={{ fontSize: 14, color: "var(--gold)", fontWeight: 600 }}>%</span>
              </div>
              <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4, letterSpacing: 0.3 }} data-testid="med-desconto-valor">
                −{brl(totais.desconto_global_valor || 0)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>Orçado Líquido</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: "var(--gold)" }} data-testid="med-total-liquido">{brl(totais.total_orcado_liquido ?? totais.total_orcado)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>Realizado</div>
              <div style={{ fontSize: 20, fontWeight: 600 }} data-testid="med-total-realizado">{brl(totais.total_realizado)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>Saldo</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: totais.saldo_total > 0 ? "#f5b942" : totais.saldo_total < 0 ? "#e35d5d" : "#5fa86c" }} data-testid="med-saldo">
                {brl(totais.saldo_total)}
              </div>
              <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2, letterSpacing: 0.3 }}>
                {totais.status_geral === "a_cobrar" && "🟡 Há valores a cobrar"}
                {totais.status_geral === "a_menos" && "🔴 Realizado abaixo do orçado"}
                {totais.status_geral === "conforme" && "🟢 Conforme"}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>Ambientes</div>
              <div style={{ fontSize: 20, fontWeight: 600 }}>{totais.ambientes_fechados}/{totais.ambientes_total} <span style={{ fontSize: 12, color: "var(--muted)" }}>fechados</span></div>
            </div>
          </div>
        </div>
      )}

      {/* Tabela lado-a-lado por ambiente (Etapas 2/3/4) */}
      {(medicoes.ambientes || []).map((amb) => (
        <AmbienteTrackingTable
          key={amb.id}
          amb={amb}
          obraId={obraId}
          api={api}
          onChanged={onReload}
        />
      ))}
    </div>
  );
}

/** Tabela de tracking de um ambiente: Orçado (locked) vs Realizado (editável). */
function AmbienteTrackingTable({ amb, obraId, api, onChanged }) {
  const fechado = !!amb.fechado;
  // Estado local pra inputs (evita re-render do form a cada digit)
  const [drafts, setDrafts] = useState({});

  const totalOrc = useMemo(
    () => (amb.itens || []).reduce((s, it) => s + Number(it.valor_final || 0), 0),
    [amb.itens]
  );
  const totalReal = useMemo(
    () => (amb.itens || []).reduce((s, it) => s + Number(it.valor_realizado || 0), 0),
    [amb.itens]
  );
  const saldo = totalReal - totalOrc;
  const status = Math.abs(saldo) < 0.01 ? "conforme" : saldo > 0 ? "a_cobrar" : "a_menos";
  const STATUS_META = {
    conforme: { color: "#5fa86c", bg: "rgba(95,168,108,0.10)", emoji: "🟢", label: "Conforme" },
    a_cobrar: { color: "#f5b942", bg: "rgba(245,185,66,0.10)", emoji: "🟡", label: "A cobrar" },
    a_menos: { color: "#e35d5d", bg: "rgba(227,93,93,0.10)", emoji: "🔴", label: "A menos" },
  };
  const sm = STATUS_META[status];

  const updateItem = async (item, field, value) => {
    if (fechado) return;
    try {
      const body = { [field]: value === "" ? 0 : Number(value) };
      await api("patch", `/obras/${obraId}/medicoes/item/${amb.id}/${item.id}`, body);
      onChanged && onChanged();
    } catch (e) {
      alert("Erro ao atualizar: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleQtdBlur = (item, raw) => {
    const value = raw === "" ? 0 : Number(String(raw).replace(",", "."));
    if (Number.isNaN(value) || value < 0) {
      alert("Quantidade inválida.");
      return;
    }
    if (value === Number(item.qtd_realizada || 0)) return; // sem mudança
    updateItem(item, "qtd_realizada", value);
  };

  const handleValorBlur = (item, raw) => {
    if (raw === "" || raw === null || raw === undefined) return;
    const value = Number(String(raw).replace(",", "."));
    if (Number.isNaN(value) || value < 0) {
      alert("Valor inválido.");
      return;
    }
    if (value === Number(item.valor_realizado || 0)) return;
    updateItem(item, "valor_realizado", value);
  };

  const fecharAmbiente = async () => {
    const msg = `Fechar ambiente "${amb.nome}"?\n\n` +
      `• Total Orçado: ${brl(totalOrc)}\n` +
      `• Total Realizado: ${brl(totalReal)}\n` +
      `• Saldo: ${brl(saldo)}  ${sm.emoji} ${sm.label}\n\n` +
      `Após fechar, os campos ficarão travados. Apenas admin pode reabrir.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api("post", `/obras/${obraId}/medicoes/ambiente/${amb.id}/fechar`);
      const sm2 = STATUS_META[r.status] || sm;
      alert(`✓ Ambiente "${amb.nome}" fechado.\n\nSaldo: ${brl(r.saldo)}  ${sm2.emoji} ${sm2.label}`);
      onChanged && onChanged();
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    }
  };

  const reabrirAmbiente = async () => {
    if (!window.confirm(`Reabrir ambiente "${amb.nome}"? Os valores realizados ficarão editáveis novamente.`)) return;
    try {
      await api("post", `/obras/${obraId}/medicoes/ambiente/${amb.id}/reabrir`);
      onChanged && onChanged();
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="panel" style={{ marginBottom: 22, padding: 0, overflow: "hidden" }} data-testid={`med-amb-${amb.id}`}>
      {/* Cabeçalho do ambiente */}
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, background: fechado ? "rgba(184,147,90,0.04)" : "transparent" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h3 style={{ margin: 0, fontSize: 17, color: "var(--gold)", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>{amb.nome}</h3>
          <span style={{ fontSize: 11, color: "var(--muted)", letterSpacing: 0.3 }}>
            {amb.itens?.length || 0} item(ns)
          </span>
          <span style={{ fontSize: 11, padding: "3px 10px", background: sm.bg, color: sm.color, border: `1px solid ${sm.color}40`, letterSpacing: 0.4 }} data-testid={`med-amb-status-${amb.id}`}>
            {sm.emoji} {sm.label}
          </span>
          {fechado && (
            <span style={{ fontSize: 10, padding: "3px 8px", background: "rgba(184,147,90,0.15)", color: "var(--gold)", letterSpacing: 0.5, textTransform: "uppercase" }}>
              🔒 Fechado por {amb.fechado_por} em {fmt(amb.fechado_em)}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {fechado ? (
            <button className="btn btn-ghost btn-sm" onClick={reabrirAmbiente} data-testid={`med-amb-reabrir-${amb.id}`} title="Apenas admin">
              Reabrir
            </button>
          ) : (
            <button
              className="btn btn-sm"
              style={{ background: "var(--gold)", color: "#0a0a0a", fontWeight: 600 }}
              onClick={fecharAmbiente}
              data-testid={`med-amb-fechar-${amb.id}`}
            >
              ✓ Fechar Ambiente
            </button>
          )}
        </div>
      </div>

      {/* Tabela lado-a-lado */}
      <div style={{ overflowX: "auto" }}>
        <table className="tbl" style={{ width: "100%", fontSize: 12, minWidth: 1100 }}>
          <thead>
            <tr style={{ background: "var(--bg-3)" }}>
              <th rowSpan={2} style={{ minWidth: 220, verticalAlign: "bottom" }}>Item</th>
              <th rowSpan={2} style={{ width: 50, verticalAlign: "bottom" }}>Un.</th>
              <th colSpan={3} style={{ textAlign: "center", borderRight: "2px solid var(--line)", padding: "8px 12px", background: "rgba(255,255,255,0.02)", letterSpacing: 0.4, color: "var(--muted)", textTransform: "uppercase", fontSize: 10 }}>
                🔒 Orçado
              </th>
              <th colSpan={4} style={{ textAlign: "center", padding: "8px 12px", background: fechado ? "rgba(184,147,90,0.08)" : "rgba(184,147,90,0.04)", letterSpacing: 0.4, color: "var(--gold)", textTransform: "uppercase", fontSize: 10 }}>
                ✏️ Realizado
              </th>
            </tr>
            <tr style={{ background: "var(--bg-3)" }}>
              <th style={{ width: 80, textAlign: "right" }}>Qtd</th>
              <th style={{ width: 100, textAlign: "right" }}>Vl. Unit.</th>
              <th style={{ width: 120, textAlign: "right", borderRight: "2px solid var(--line)" }}>Vl. Final</th>
              <th style={{ width: 90, textAlign: "right" }}>Qtd</th>
              <th style={{ width: 120, textAlign: "right" }}>Vl. Realizado</th>
              <th style={{ width: 80, textAlign: "right" }}>Δ Qtd</th>
              <th style={{ width: 110, textAlign: "right" }}>Δ Valor</th>
            </tr>
          </thead>
          <tbody>
            {(amb.itens || []).map((it) => {
              const qtdReal = Number(it.qtd_realizada || 0);
              const qtdOrc = Number(it.qtd_orcada || 0);
              const valOrc = Number(it.valor_final || 0);
              const valReal = Number(it.valor_realizado || 0);
              const dQtd = qtdReal - qtdOrc;
              const dVal = valReal - valOrc;
              const itemStatus = Math.abs(dVal) < 0.01 ? "conforme" : dVal > 0 ? "a_cobrar" : "a_menos";
              const ism = STATUS_META[itemStatus];
              const draftKey = it.id;
              const draft = drafts[draftKey] || {};
              return (
                <tr key={it.id}>
                  <td style={{ background: "var(--bg-3)", color: "var(--ink-2)" }}>{it.item}</td>
                  <td style={{ background: "var(--bg-3)", color: "var(--ink-3)" }}>{it.unidade}</td>
                  {/* Orçado (locked, dark) */}
                  <td style={{ textAlign: "right", background: "var(--bg-3)", color: "var(--ink-3)" }}>{qtdOrc.toLocaleString("pt-BR")}</td>
                  <td style={{ textAlign: "right", background: "var(--bg-3)", color: "var(--ink-3)" }}>{brl(it.valor_unitario)}</td>
                  <td style={{ textAlign: "right", background: "var(--bg-3)", color: "var(--gold)", fontWeight: 600, borderRight: "2px solid var(--line)" }}>{brl(valOrc)}</td>
                  {/* Realizado (editável) */}
                  <td style={{ textAlign: "right", padding: 4 }}>
                    {fechado ? (
                      <span style={{ paddingRight: 8 }}>{qtdReal.toLocaleString("pt-BR")}</span>
                    ) : (
                      <input
                        type="number" step="0.01" min="0"
                        defaultValue={qtdReal || ""}
                        key={`qtd-${it.id}-${qtdReal}`}
                        className="form-input form-input-sm"
                        onBlur={(e) => handleQtdBlur(it, e.target.value)}
                        onChange={(e) => setDrafts(d => ({ ...d, [draftKey]: { ...d[draftKey], qtd: e.target.value } }))}
                        style={{ textAlign: "right", padding: "4px 6px", width: "100%" }}
                        data-testid={`med-qtd-real-${it.id}`}
                      />
                    )}
                  </td>
                  <td style={{ textAlign: "right", padding: 4 }}>
                    {fechado ? (
                      <span style={{ paddingRight: 8, color: "var(--gold)", fontWeight: 600 }}>{brl(valReal)}</span>
                    ) : (
                      <input
                        type="number" step="0.01" min="0"
                        defaultValue={valReal || ""}
                        key={`val-${it.id}-${valReal}`}
                        className="form-input form-input-sm"
                        onBlur={(e) => handleValorBlur(it, e.target.value)}
                        onChange={(e) => setDrafts(d => ({ ...d, [draftKey]: { ...d[draftKey], val: e.target.value } }))}
                        placeholder={brl(qtdReal * Number(it.valor_unitario || 0) * (1 - Number(it.desconto_pct || 0) / 100))}
                        style={{ textAlign: "right", padding: "4px 6px", width: "100%", color: "var(--gold)", fontWeight: 600 }}
                        data-testid={`med-val-real-${it.id}`}
                      />
                    )}
                  </td>
                  {/* Delta Qtd */}
                  <td style={{ textAlign: "right", color: dQtd > 0 ? "#f5b942" : dQtd < 0 ? "#e35d5d" : "var(--ink-3)", fontVariantNumeric: "tabular-nums" }}>
                    {dQtd === 0 ? "—" : (dQtd > 0 ? "+" : "") + dQtd.toLocaleString("pt-BR", { maximumFractionDigits: 4 })}
                  </td>
                  {/* Delta Valor + status emoji */}
                  <td style={{ textAlign: "right", color: ism.color, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                    <span title={ism.label}>{ism.emoji}</span> {dVal === 0 ? "—" : (dVal > 0 ? "+" : "") + brl(Math.abs(dVal)).replace("R$", "R$")}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: "var(--bg-3)", fontWeight: 600 }}>
              <td colSpan={4} style={{ textAlign: "right", padding: "10px 12px", color: "var(--ink-3)", letterSpacing: 0.3, textTransform: "uppercase", fontSize: 10 }}>Subtotal:</td>
              <td style={{ textAlign: "right", color: "var(--gold)", borderRight: "2px solid var(--line)" }} data-testid={`med-amb-tot-orc-${amb.id}`}>{brl(totalOrc)}</td>
              <td colSpan={1}></td>
              <td style={{ textAlign: "right", color: "var(--gold)" }} data-testid={`med-amb-tot-real-${amb.id}`}>{brl(totalReal)}</td>
              <td></td>
              <td style={{ textAlign: "right", color: sm.color, fontWeight: 700 }} data-testid={`med-amb-saldo-${amb.id}`}>
                {sm.emoji} {brl(saldo)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ========== ETAPA 6: TERMO DE MEDIÇÃO FINAL ========== */

/** Botão que aparece no header do MedicoesSavedView. Gera ou regenera o termo. */
function TermoActionButton({ medicoes, obraId, api, onChanged }) {
  const [gen, setGen] = useState(false);
  const termo = medicoes?.termo_final;

  const gerar = async () => {
    const isRegen = !!termo;
    const msg = isRegen
      ? "Regenerar o Termo de Medição Final?\n\nUma nova versão será criada com os dados atuais. Aceites do cliente neste termo serão preservados, mas o cliente verá a nova versão. Será enviado um email de notificação se o cliente tiver email cadastrado."
      : "Gerar o Termo de Medição Final em PDF?\n\nO documento ficará disponível para visualização e compartilhamento. Se o cliente tiver email cadastrado, ele receberá uma notificação automática com link para o Signature Book.";
    if (!window.confirm(msg)) return;
    setGen(true);
    try {
      const r = await api("post", `/obras/${obraId}/medicoes/termo/gerar`, { enviar_email: true });
      // Notifica resultado do email
      if (r.email?.enviado) {
        alert(`✓ Termo v${r.termo_final.version} gerado!\n\n📧 Email automático enviado para ${r.email.para}.`);
      } else if (r.email?.motivo) {
        alert(`✓ Termo v${r.termo_final.version} gerado!\n\n⚠️ Email NÃO foi enviado: ${r.email.motivo}`);
      }
      onChanged && onChanged();
    } catch (e) {
      alert("Erro ao gerar termo: " + (e?.response?.data?.detail || e.message));
    } finally {
      setGen(false);
    }
  };

  return (
    <button
      className="btn btn-sm"
      style={{ background: termo ? "transparent" : "var(--gold)", color: termo ? "var(--gold)" : "#0a0a0a", border: "1px solid var(--gold)", fontWeight: 600 }}
      onClick={gerar}
      disabled={gen}
      data-testid="termo-gerar-btn"
    >
      {gen ? "Gerando…" : termo ? <><RefreshCw size={12} /> Regenerar Termo</> : <><FileText size={12} /> Gerar Termo Final</>}
    </button>
  );
}

/** Painel que aparece logo após o Resumo, mostrando o termo gerado e ações. */
function TermoFinalPanel({ medicoes, obraId, api, onChanged }) {
  const termo = medicoes?.termo_final;
  const aceiteCliente = medicoes?.termo_aceite_cliente;
  const [views, setViews] = useState(null);

  useEffect(() => {
    if (!termo) return;
    api("get", `/obras/${obraId}/termo-views`)
      .then(r => setViews(r.total))
      .catch(() => setViews(null));
  }, [api, obraId, termo]);

  if (!termo) return null;

  const removerTermo = async () => {
    if (!window.confirm("Remover o Termo de Medição Final?\n\nO PDF não será apagado do storage, mas a obra voltará a aparecer como 'sem termo'. Aceites do cliente serão removidos.")) return;
    try {
      await api("delete", `/obras/${obraId}/medicoes/termo`);
      onChanged && onChanged();
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    }
  };

  // URLs
  const pdfUrl = fileUrl(termo.url);
  // Ler public_token via medicoes? Ele não está aqui — vamos buscar pela URL pública genérica
  // O obra.public_token chega via prop separada se necessário; aqui só compartilhamos o PDF direto
  const whatsappText = encodeURIComponent(
    `Olá! Segue o Termo de Medição Final da obra. Acesse o PDF: ${pdfUrl}\n\nQualquer dúvida, estamos à disposição.\n\nCAMASA Mármores & Design`
  );
  const whatsappLink = `https://wa.me/?text=${whatsappText}`;

  return (
    <div className="panel" style={{ marginBottom: 22, padding: 18, borderColor: "var(--gold)", background: "rgba(184,147,90,0.04)" }} data-testid="termo-final-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1, minWidth: 280 }}>
          <div style={{ width: 48, height: 48, background: "var(--gold)", color: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <FileText size={24} strokeWidth={1.5} />
          </div>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 0.4, color: "var(--gold)", textTransform: "uppercase" }}>Documento Final</div>
            <div style={{ fontSize: 17, fontFamily: "'Fraunces',serif", fontStyle: "italic", color: "var(--ink)", marginTop: 2 }}>
              Termo de Medição Final · v{termo.version}
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
              Gerado em {fmt(termo.gerado_em)} por {termo.gerado_por} · {Math.round((termo.size_bytes || 0) / 1024)}KB
              {views !== null && (
                <span style={{ marginLeft: 10, color: "var(--gold)" }} data-testid="termo-views-count">
                  · 👁 {views} visualização(ões)
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn btn-sm" style={{ background: "var(--gold)", color: "#0a0a0a", fontWeight: 600 }} data-testid="termo-view-btn">
            <Download size={12} /> Visualizar PDF
          </a>
          <a href={whatsappLink} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ borderColor: "#25d366", color: "#25d366" }} data-testid="termo-wa-btn">
            <MessageCircle size={12} /> WhatsApp
          </a>
          <button className="btn btn-ghost btn-sm" style={{ color: "#c47a7a", borderColor: "#4a2a2a" }} onClick={removerTermo} data-testid="termo-remove-btn" title="Remove o registro do termo (PDF fica no storage)">
            <X size={12} /> Remover
          </button>
        </div>
      </div>
      {aceiteCliente ? (
        <div style={{ marginTop: 14, padding: 12, background: "rgba(95,168,108,0.08)", border: "1px solid var(--verde-2)", display: "flex", alignItems: "center", gap: 12 }} data-testid="termo-aceite-cliente">
          <CheckCircle2 size={20} color="var(--verde-2)" />
          <div>
            <div style={{ fontSize: 10, letterSpacing: 0.3, color: "var(--verde-2)", textTransform: "uppercase" }}>Termo aceito pelo cliente</div>
            <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 16, color: "var(--ink)", marginTop: 2 }}>
              {aceiteCliente.nome}
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
              {fmt(aceiteCliente.data)} · {new Date(aceiteCliente.data).toLocaleTimeString("pt-BR")} · IP {aceiteCliente.ip || "—"}
            </div>
            {aceiteCliente.observacao && (
              <div style={{ fontSize: 11, color: "var(--ink-2)", marginTop: 6, fontStyle: "italic" }}>
                "{aceiteCliente.observacao}"
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 14, padding: 10, fontSize: 11, color: "var(--muted)", letterSpacing: 0.3, textTransform: "uppercase", textAlign: "center", borderTop: "1px solid var(--line)" }}>
          ⏳ Aguardando aceite do cliente
        </div>
      )}
    </div>
  );
}

const brlFmt = (v) => "R$ " + (Number(v) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function OrcamentosExtrasSection({ obraId, extras = [], totais, api, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [nome, setNome] = useState("");
  const [valor, setValor] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const addOrcamento = async () => {
    setErr("");
    const nomeT = nome.trim();
    const valNum = parseFloat((valor || "").toString().replace(",", "."));
    if (!nomeT) { setErr("Informe o nome do orçamento"); return; }
    if (isNaN(valNum) || valNum < 0) { setErr("Informe um valor válido"); return; }
    setSaving(true);
    try {
      await api("post", `/obras/${obraId}/medicoes/orcamento-extra`, { nome: nomeT, valor: valNum });
      setNome(""); setValor(""); setAdding(false);
      onChanged && onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Erro ao adicionar");
    } finally { setSaving(false); }
  };

  const removeOrcamento = async (orcId, nomeRef) => {
    if (!window.confirm(`Remover o orçamento "${nomeRef}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api("delete", `/obras/${obraId}/medicoes/orcamento-extra/${orcId}`);
      onChanged && onChanged();
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e?.message));
    }
  };

  const totalExtras = (totais?.total_orcamentos_extras) ?? extras.reduce((s, o) => s + (Number(o.valor) || 0), 0);
  const totalLiquidoPdf = totais?.total_orcado_liquido ?? 0;
  const totalConsolidado = totais?.total_orcado_consolidado ?? (totalLiquidoPdf + totalExtras);
  const totalRealizado = totais?.total_realizado ?? 0;
  const diferenca = totalRealizado - totalConsolidado;

  return (
    <div className="panel" style={{ padding: 18, marginTop: 16 }} data-testid="orcamentos-extras-section">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--gold)" }}>
            Orçamentos adicionais
          </h3>
          <p className="page-sub" style={{ fontSize: 12, margin: "4px 0 0" }}>
            Some orçamentos avulsos (ex: nichos, baguete) ao orçado total da obra.
          </p>
        </div>
        {!adding && (
          <button
            className="btn btn-sm"
            onClick={() => { setAdding(true); setErr(""); }}
            data-testid="orcamento-extra-add-btn"
            style={{ background: "var(--gold)", color: "#0a0a0a", fontWeight: 600 }}
          >
            + Adicionar Orçamento
          </button>
        )}
      </div>

      {adding && (
        <div style={{ padding: 14, background: "rgba(184,147,90,0.06)", border: "1px solid rgba(184,147,90,0.25)", marginBottom: 14 }} data-testid="orcamento-extra-form">
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto auto", gap: 10, alignItems: "end" }}>
            <div className="field">
              <label>Nome do orçamento</label>
              <input className="input" value={nome} onChange={e => setNome(e.target.value.slice(0, 120))} placeholder="Ex: Nichos / Baguete / Complementar"
                data-testid="orcamento-extra-nome" autoFocus />
            </div>
            <div className="field">
              <label>Valor (R$)</label>
              <input className="input" value={valor} onChange={e => setValor(e.target.value.replace(/[^0-9.,]/g, ""))}
                placeholder="0,00" inputMode="decimal" data-testid="orcamento-extra-valor" />
            </div>
            <button className="btn btn-sm" onClick={addOrcamento} disabled={saving} data-testid="orcamento-extra-confirm"
              style={{ background: "var(--gold)", color: "#0a0a0a", fontWeight: 600 }}>
              {saving ? "Salvando…" : "Salvar"}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setAdding(false); setNome(""); setValor(""); setErr(""); }} disabled={saving}>
              Cancelar
            </button>
          </div>
          {err && <div style={{ marginTop: 8, color: "#e35d5d", fontSize: 12 }} data-testid="orcamento-extra-err">⚠ {err}</div>}
        </div>
      )}

      {extras.length === 0 ? (
        <div style={{ padding: 18, textAlign: "center", color: "var(--muted)", fontSize: 12, border: "1px dashed var(--line)" }} data-testid="orcamentos-extras-empty">
          Nenhum orçamento adicional. Use o botão acima para incluir orçamentos avulsos (ex: nichos, baguete).
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {extras.map((o, idx) => (
            <div key={o.id} data-testid={`orcamento-extra-row-${idx}`} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
              padding: "10px 14px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)",
              borderLeft: "2px solid var(--gold)",
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: "var(--ink)", fontWeight: 600 }}>{o.nome}</div>
                {o.created_by_name && (
                  <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
                    Adicionado por {o.created_by_name}{o.created_at ? ` · ${new Date(o.created_at).toLocaleDateString("pt-BR")}` : ""}
                  </div>
                )}
              </div>
              <div style={{ fontVariantNumeric: "tabular-nums", fontSize: 14, color: "var(--gold)", fontWeight: 600, whiteSpace: "nowrap" }}>
                {brlFmt(o.valor)}
              </div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => removeOrcamento(o.id, o.nome)}
                data-testid={`orcamento-extra-remove-${idx}`}
                title="Remover este orçamento"
                style={{ padding: "4px 10px" }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {(extras.length > 0 || totalLiquidoPdf > 0) && (
        <div style={{ marginTop: 16, padding: 14, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(184,147,90,0.2)" }} data-testid="orcamentos-totalizador">
          <div style={{ fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 10 }}>
            Total consolidado
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 6, columnGap: 16, fontSize: 13 }}>
            {totalLiquidoPdf > 0 && (
              <>
                <div style={{ color: "var(--muted)" }}>Orçamento principal (PDF, líquido)</div>
                <div style={{ fontVariantNumeric: "tabular-nums", color: "var(--ink-2)" }}>{brlFmt(totalLiquidoPdf)}</div>
              </>
            )}
            {totalExtras > 0 && (
              <>
                <div style={{ color: "var(--muted)" }}>Soma dos {extras.length} orçamento(s) adicional(is)</div>
                <div style={{ fontVariantNumeric: "tabular-nums", color: "var(--ink-2)" }}>{brlFmt(totalExtras)}</div>
              </>
            )}
            <div style={{ gridColumn: "1/-1", borderTop: "1px solid var(--line)", margin: "6px 0" }} />
            <div style={{ fontWeight: 600, color: "var(--gold)" }} data-testid="orcamento-consolidado-label">Total orçado consolidado</div>
            <div data-testid="orcamento-consolidado-valor" style={{ fontVariantNumeric: "tabular-nums", color: "var(--gold)", fontWeight: 700, fontSize: 16 }}>
              {brlFmt(totalConsolidado)}
            </div>
            {totalRealizado > 0 && (
              <>
                <div style={{ color: "var(--muted)" }}>Total realizado (medições)</div>
                <div style={{ fontVariantNumeric: "tabular-nums", color: "var(--ink-2)" }}>{brlFmt(totalRealizado)}</div>
                <div style={{ color: diferenca > 0 ? "#e3c66c" : (diferenca < 0 ? "#e35d5d" : "#5fa86c"), fontWeight: 600 }}>
                  Diferença
                </div>
                <div data-testid="orcamento-diferenca" style={{
                  fontVariantNumeric: "tabular-nums",
                  color: diferenca > 0 ? "#e3c66c" : (diferenca < 0 ? "#e35d5d" : "#5fa86c"),
                  fontWeight: 700,
                }}>
                  {diferenca > 0 ? "+ " : (diferenca < 0 ? "− " : "")}
                  {brlFmt(Math.abs(diferenca))}
                  <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 400, color: "var(--muted)", letterSpacing: ".05em", textTransform: "uppercase" }}>
                    {diferenca > 0 ? "acima" : (diferenca < 0 ? "abaixo" : "conforme")}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

